import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { TAGS } from '~/constants/edf';
import edfService from '~/services/Edf';
import { NUMBER_OF_SLIDES_IN_CACHE } from '~/constants/chart';

import { initEdf, reset } from '../edf/loadingFiles';
import {
    createGetChannelDataConfig,
    getAllChannelDataPromises,
    getAvailableZoomLevels,
    getPreviousZoomLevel,
    visibleAnnotations,
    getMinMaxForProp,
    getNextZoomLevel,
    handleZeroValues,
} from './helpers';

/**
 *    Here is a kind of circular queue implementation.
 *
 *    On first step we initialize min cache, it equals NUMBER_OF_SLIDES_IN_CACHE / 2 rounded up.
 *    Pre-conditions:
 *    - NUMBER_OF_SLIDES_IN_CACHE = 5;
 *    - zoomLevel = 120;
 *    So, 120 * RoundUp(5 / 2) = 360;
 *
 *    |-----(120)-----(240)-----(360)
 *
 *    Each next getNextSlice() call add to cache 120 bytes of the data until total data range <= NUMBER_OF_SLIDES_IN_CACHE
 *
 *    dispatch(getNextSlice());
 *    |-----(120)-----(240)-----(360)-----(480)
 *    dispatch(getNextSlice());
 *    |-----(120)-----(240)-----(360)-----(480)-----(600)
 *
 *    Once data range === NUMBER_OF_SLIDES_IN_CACHE further getNextSlice call will cut [0, zoomLevel] data length
 *    and add next data to the end.
 *
 *    dispatch(getNextSlice());
 *    (120)-----(240)-----(360)-----(480)-----(600)-----(720)
 *    dispatch(getNextSlice());
 *    (240)-----(360)-----(480)-----(600)-----(720)-----(840)
 */

const initialChannelConfig = {
    appliedTransformations: ['physical', 'format', 'downsample'],
    appliedAnnotations: [],
    transformationsConfig: {
        downsample: {
            threshold: 3600,
        },
    },

    visible: true,
    color: 'black',
    position: null,
    isMonotoneInterpolationEnabled: true,
};
Object.freeze(initialChannelConfig);

const initialState = {
    visibleAnnotations,
    zoomLevel: 1800, // zoomLevel represents epoch size
    availableZoomLevels: [],
    xScrollPosition: 0,
    isLoading: false,
    maxDataSize: 0,
    range: [null, null],
    channelsData: [],
    channelsConfig: {},
    skipChannelIndex: null,
    annotationsConfig: {
        [TAGS.COMMENT]: {
            color: 'rgb(255 140 0 / 0.6)',
        },
        [TAGS.RESPIRATORY]: {
            color: 'rgb(255 0 0 / 0.6)',
        },
        [TAGS.CENTRAL]: {
            color: 'rgb(255 0 0 / 0.2)',
        },
        [TAGS.OBSTRUCTIVE]: {
            color: 'rgb(255 0 0 / 0.2)',
        },
        [TAGS.APNEA]: {
            color: 'rgb(255 0 0 / 0.2)',
        },
        [TAGS.HYPOPNEA]: {
            color: 'rgb(255 0 0 / 0.2)',
        },
        [TAGS.DESATURATION]: {
            color: 'rgb(0 179 255 / 0.2)',
        },
        [TAGS.UPRIGHT]: {
            color: 'rgb(2 178 152 / 0.2)',
        },
        [TAGS.SUPINE]: {
            color: 'rgb(2 178 152 / 0.2)',
        },
        [TAGS.NONSUPINE]: {
            color: 'rgb(2 178 152 / 0.2)',
        },
        [TAGS.DROPOUT]: {
            color: 'rgb(22 17 0 / 0.2)',
        },
        [TAGS.ARTIFACT]: {
            color: 'rgb(215 9 161 / 0.2)',
        },
        [TAGS.MOTION]: {
            color: 'rgb(215 9 161 / 0.2)',
        },
        [TAGS.HYPNOGRAM]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
        [TAGS.N1]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
        [TAGS.N2]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
        [TAGS.N3]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
        [TAGS.REM]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
        [TAGS.WAKE]: {
            color: 'rgb(63 32 238 / 0.2)',
        },
    },
    quickViewChannelsData: {},
};
Object.freeze(initialState);

let tolerance = 0;

const chartSlice = createSlice({
    name: 'chart',
    initialState,
    reducers: {
        setZoomLevel: (state, { payload }) => {
            state.zoomLevel = payload;
            state.channelsData.forEach((channel) => {
                channel.dataset = [];
            });
            state.range = [null, null];
            state.xScrollPosition =
                state.xScrollPosition + state.zoomLevel > state.maxDataSize
                    ? state.maxDataSize - state.zoomLevel
                    : state.xScrollPosition;
            tolerance = Math.ceil(state.maxDataSize / payload) * payload - state.maxDataSize;
        },
        setRange: (state, { payload }) => {
            state.range = [payload.start, payload.end];
        },
        setXScrollPosition: (state, { payload }) => {
            state.xScrollPosition = payload;
        },
        toggleMonotoneInterpolation: (state, { payload }) => {
            const channel = state.channelsConfig[payload.id];

            if (channel) {
                channel.isMonotoneInterpolationEnabled = !channel.isMonotoneInterpolationEnabled;
            }
        },
        toggleAppliedAnnotation: (state, { payload }) => {
            const channel = state.channelsConfig[payload.id];

            if (channel) {
                if (!channel.appliedAnnotations.includes(payload.annotationType)) {
                    channel.appliedAnnotations.push(payload.annotationType);
                } else {
                    channel.appliedAnnotations = channel.appliedAnnotations.filter(
                        (type) => type !== payload.annotationType
                    );
                }
            }
        },
        setChannelYAxisLimits: (state, { payload: { currentMin, currentMax, channelName } }) => {
            state.channelsData.forEach((channel) => {
                if (channel.label === channelName) {
                    const {
                        axes: {
                            y: { min, max },
                        },
                    } = channel;

                    if (currentMin) {
                        channel.axes.y.currentMin = currentMin < min ? min : currentMin;
                    }

                    if (currentMax) {
                        channel.axes.y.currentMax = currentMax > max ? max : currentMax;
                    }
                }
            });
        },
        setChannelsOrder: (state, { payload }) => {
            payload.order.forEach((id, index) => {
                state.channelsConfig[id].position = index;
            });
        },
        increaseChannelYLimits: (state, { payload: { channelLabel } }) => {
            const channel = state.channelsData.find(({ label }) => label === channelLabel);

            if (channel === undefined) {
                return;
            }

            const {
                axes: {
                    y: { min, max },
                },
            } = channel;

            const step = (max - min) / 50;

            let nextMin = channel.axes.y.currentMin + step;
            let nextMax = channel.axes.y.currentMax - step;

            nextMin = Number(nextMin.toFixed(3));
            nextMax = Number(nextMax.toFixed(3));

            if (nextMin >= nextMax) {
                return;
            }

            channel.axes.y.currentMin = nextMin;
            channel.axes.y.currentMax = nextMax;
        },
        decreaseChannelYLimits: (state, { payload: { channelLabel } }) => {
            const channel = state.channelsData.find(({ label }) => label === channelLabel);

            if (channel === undefined) {
                return;
            }

            const {
                axes: {
                    y: { min, max },
                },
            } = channel;

            const step = (max - min) / 50;

            let nextMin = channel.axes.y.currentMin - step;
            let nextMax = channel.axes.y.currentMax + step;

            nextMin = Number(nextMin.toFixed(3));
            nextMax = Number(nextMax.toFixed(3));

            channel.axes.y.currentMin = Math.max(min, nextMin);
            channel.axes.y.currentMax = Math.min(max, nextMax);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initEdf, (state, { payload: { headers } }) => {
                const isConfigEmpty = !Object.keys(state.channelsConfig).length;

                state.maxDataSize = headers.recordingLength;
                state.availableZoomLevels = getAvailableZoomLevels(headers.recordingLength);
                state.skipChannelIndex = headers.edfAnnotationsIndex;

                for (let i = 0; i < headers.number_of_signals; i++) {
                    if (i === state.skipChannelIndex) {
                        continue;
                    }

                    // TODO: we need to identify channels somehow,
                    //  in case if we get new set of signals (less/more/another) we would prefer to reset channels config
                    if (isConfigEmpty) {
                        state.channelsConfig[i] = {
                            ...initialChannelConfig,
                            position: i,
                        };
                    }

                    state.channelsData.push({
                        id: i,
                        fs: headers.fs[i],
                        label: headers.labels[i],
                        unit: headers.physical_dimensions[i],
                        dataset: [],
                        axes: {
                            y: {
                                currentMin: headers.physical_minimums[i],
                                currentMax: headers.physical_maximums[i],
                                min: headers.physical_minimums[i],
                                max: headers.physical_maximums[i],
                            },
                        },
                    });
                }
            })
            .addCase(reset, () => initialState)
            .addCase(getWholeDataset.fulfilled, (state, { payload }) => {
                payload.forEach((channel, index) => {
                    if (index === state.skipChannelIndex) {
                        return;
                    }

                    if (channel !== undefined) {
                        const parsedPayload = handleZeroValues(
                            channel,
                            state.channelsData[index].label
                        );
                        state.quickViewChannelsData[index] = parsedPayload;

                        const { min, max } = getMinMaxForProp('y', parsedPayload);
                        state.channelsData[index].axes.y.currentMin = min;
                        state.channelsData[index].axes.y.currentMax = max;
                    }
                });
            })
            .addCase(getNextSlice.fulfilled, (state, { payload }) => {
                state.channelsData.forEach((channel, index) => {
                    let sliceIndex = 0;

                    for (let i = 0; i < channel.dataset.length; i++) {
                        if (channel.dataset[i].x >= state.range[0]) {
                            sliceIndex = i;
                            break;
                        }
                    }

                    let flatted = payload[index];

                    if (Array.isArray(payload[0][0])) {
                        flatted = [];
                        for (const set of payload) {
                            flatted.push(...handleZeroValues(set[index], channel.label));
                        }
                    }

                    channel.dataset = [...channel.dataset.slice(sliceIndex), ...flatted];
                });
            })
            .addCase(getPreviousSlice.fulfilled, (state, { payload }) => {
                state.channelsData.forEach((channel, index) => {
                    let sliceIndex = 0;

                    for (let i = channel.dataset.length - 1; i >= 0; i--) {
                        if (channel.dataset[i].x <= state.range[1]) {
                            sliceIndex = i;
                            break;
                        }
                    }

                    let flatted = payload[index];

                    if (Array.isArray(payload[0][0])) {
                        flatted = [];
                        for (const set of payload) {
                            flatted.push(...handleZeroValues(set[index], channel.label));
                        }
                    }

                    channel.dataset = [...flatted, ...channel.dataset.slice(0, sliceIndex)];
                });
            })
            .addMatcher(
                isAnyOf(
                    jumpToFirstSlice.fulfilled,
                    jumpToLastSlice.fulfilled,
                    jumpForCertainTime.fulfilled
                ),
                (state, { payload }) => {
                    state.channelsData.forEach((channel, index) => {
                        let flatted = payload[index];

                        if (Array.isArray(payload[0][0])) {
                            flatted = [];
                            for (const set of payload) {
                                flatted.push(...handleZeroValues(set[index], channel.label));
                            }
                        }

                        channel.dataset = flatted;
                    });
                }
            );
    },
});

export const getNextSlice = createAsyncThunk('chart/getNextSlice', async (_, thunkAPI) => {
    let {
        channelsConfig,
        maxDataSize,
        range: [cacheSliceStart, cacheSliceEnd],
        zoomLevel,
        xScrollPosition,
    } = thunkAPI.getState().app.chart;
    const config = createGetChannelDataConfig(channelsConfig);

    if (cacheSliceStart === null) {
        const _start = xScrollPosition - zoomLevel * Math.floor(NUMBER_OF_SLIDES_IN_CACHE / 2);
        const _end = xScrollPosition + zoomLevel * Math.ceil(NUMBER_OF_SLIDES_IN_CACHE / 2);
        cacheSliceStart = _start <= 0 ? 0 : _start;
        cacheSliceEnd = _end > maxDataSize ? maxDataSize : _end;

        const promises = getAllChannelDataPromises(
            cacheSliceStart,
            cacheSliceEnd,
            zoomLevel,
            config
        );

        return Promise.all(promises).then(
            thunkAPI.dispatch(
                chartSlice.actions.setRange({ start: cacheSliceStart, end: cacheSliceEnd })
            )
        );
    }

    const nextCacheSliceEnd =
        cacheSliceEnd + zoomLevel > maxDataSize ? maxDataSize : cacheSliceEnd + zoomLevel;
    // start moving start point only when a buffer is full (range more than [0, zoomLevel * NUMBER_OF_SLIDES_IN_CACHE]
    const nextCacheSliceStart =
        nextCacheSliceEnd - zoomLevel * NUMBER_OF_SLIDES_IN_CACHE <= 0
            ? 0
            : nextCacheSliceEnd - zoomLevel * NUMBER_OF_SLIDES_IN_CACHE;
    if (nextCacheSliceEnd <= cacheSliceEnd) {
        return Promise.reject('Requested data range is already in the cache!');
    }

    thunkAPI.dispatch(
        chartSlice.actions.setRange({ start: nextCacheSliceStart, end: nextCacheSliceEnd })
    );

    const promises = getAllChannelDataPromises(cacheSliceEnd, nextCacheSliceEnd, zoomLevel, config);
    return Promise.all(promises);
});
export const getPreviousSlice = createAsyncThunk('chart/getPreviousSlice', async (_, thunkAPI) => {
    let {
        channelsConfig,
        range: [cacheSliceStart, cacheSliceEnd],
        zoomLevel,
    } = thunkAPI.getState().app.chart;
    const config = createGetChannelDataConfig(channelsConfig);

    const nextCacheSliceStart =
        cacheSliceStart - zoomLevel * Math.floor(NUMBER_OF_SLIDES_IN_CACHE / 2) + tolerance < 0
            ? 0
            : cacheSliceStart - zoomLevel;

    // start moving start point only when a buffer is full (range more than [0, zoomLevel * NUMBER_OF_SLIDES_IN_CACHE]
    const nextCacheSliceEnd =
        cacheSliceEnd - nextCacheSliceStart > NUMBER_OF_SLIDES_IN_CACHE * zoomLevel
            ? cacheSliceEnd - zoomLevel
            : cacheSliceEnd;

    const nextSliceEnd = Math.min(nextCacheSliceStart + zoomLevel, cacheSliceStart);

    if (nextCacheSliceStart >= cacheSliceStart && nextSliceEnd <= cacheSliceEnd) {
        return Promise.reject('Requested data rage is already in the cache!');
    }

    thunkAPI.dispatch(
        chartSlice.actions.setRange({
            start: nextCacheSliceStart,
            end: nextCacheSliceEnd,
        })
    );

    const promises = getAllChannelDataPromises(
        nextCacheSliceStart,
        nextSliceEnd,
        zoomLevel,
        config
    );

    return Promise.all(promises);
});
export const getWholeDataset = createAsyncThunk(
    'chart/getWholeDataset',
    async ({ channelLabel }, thunkAPI) => {
        let { maxDataSize, channelsData } = thunkAPI.getState().app.chart;
        const channel = channelsData.find(
            ({ label }) => label.toLowerCase().trim() === channelLabel.toLowerCase().trim()
        );

        if (channel === undefined) {
            return;
        }

        const channelId = channel.id;

        const config = {
            [channelId]: {
                downsample: {
                    threshold: 3600,
                },
                format: true,
                physical: true,
            },
        };
        config.ignoredChannels = channelsData.map(({ id }) => id).filter((id) => id !== channelId);

        return edfService.getAllChannelData(0, maxDataSize, config);
    }
);
export const jumpToNextSlice = createAsyncThunk('chart/jumpToNextSlice', async (_, thunkAPI) => {
    const { zoomLevel, maxDataSize, xScrollPosition } = thunkAPI.getState().app.chart;

    const nextPosition =
        xScrollPosition + zoomLevel * 2 >= maxDataSize
            ? maxDataSize - zoomLevel
            : xScrollPosition + zoomLevel;

    return thunkAPI
        .dispatch(getNextSlice())
        .then(thunkAPI.dispatch(chartSlice.actions.setXScrollPosition(nextPosition)));
});
export const jumpToPreviousSlice = createAsyncThunk(
    'chart/jumpToPrevSlice',
    async (_, thunkAPI) => {
        const {
            range: [start, end],
            zoomLevel,
            xScrollPosition,
        } = thunkAPI.getState().app.chart;

        if (end === zoomLevel * 3) {
            return;
        }

        const nextPosition = xScrollPosition - zoomLevel < 0 ? 0 : xScrollPosition - zoomLevel;

        if (nextPosition >= start && nextPosition <= end) {
            thunkAPI.dispatch(getPreviousSlice());
        }

        thunkAPI.dispatch(chartSlice.actions.setXScrollPosition(nextPosition));
    }
);
export const jumpForCertainTime = createAsyncThunk(
    'chart/jumpForCertainTime',
    async ({ time, setInTheMiddle = false }, thunkAPI) => {
        const { zoomLevel, channelsConfig, maxDataSize } = thunkAPI.getState().app.chart;
        const config = createGetChannelDataConfig(channelsConfig);

        const lastStartPosition =
            maxDataSize - zoomLevel * Math.floor(NUMBER_OF_SLIDES_IN_CACHE / 2);
        const _start = time - zoomLevel * Math.floor(NUMBER_OF_SLIDES_IN_CACHE / 2);
        const start = time > lastStartPosition ? lastStartPosition : Math.max(0, _start);
        const _end = time + zoomLevel * Math.ceil(NUMBER_OF_SLIDES_IN_CACHE / 2);
        const end = Math.min(_end, maxDataSize);

        let newTime = time;
        const isMoreThenRecord = time + zoomLevel >= maxDataSize;
        const isLessThenRecord = time - zoomLevel < 0;

        if (isMoreThenRecord) {
            newTime = maxDataSize - zoomLevel;
        }

        if (isLessThenRecord) {
            newTime = 0;
        }

        const scrollPosition = setInTheMiddle
            ? isMoreThenRecord || isLessThenRecord
                ? newTime
                : newTime - zoomLevel / 2
            : newTime;

        const promises = getAllChannelDataPromises(start, end, zoomLevel, config);
        return Promise.all(promises)
            .then(thunkAPI.dispatch(chartSlice.actions.setRange({ start: start, end: end })))
            .then(thunkAPI.dispatch(chartSlice.actions.setXScrollPosition(scrollPosition)));
    }
);
export const zoomInOut = createAsyncThunk(
    'chart/zoomInOut',
    async ({ time, isZoomIn }, thunkAPI) => {
        const { zoomLevel } = thunkAPI.getState().app.chart;
        let nextZoomLevel = isZoomIn
            ? getPreviousZoomLevel(zoomLevel)
            : getNextZoomLevel(zoomLevel);

        if (nextZoomLevel === null) {
            return;
        }

        return thunkAPI
            .dispatch(chartSlice.actions.setZoomLevel(nextZoomLevel))
            .then(thunkAPI.dispatch(jumpForCertainTime({ time, setInTheMiddle: true })));
    }
);
export const jumpToFirstSlice = createAsyncThunk('chart/jumpToFirstSlice', async (_, thunkAPI) => {
    let { channelsConfig, maxDataSize, zoomLevel } = thunkAPI.getState().app.chart;
    const config = createGetChannelDataConfig(channelsConfig);

    let start = 0;
    let end =
        zoomLevel * Math.ceil(NUMBER_OF_SLIDES_IN_CACHE / 2) > maxDataSize
            ? maxDataSize
            : zoomLevel * Math.ceil(NUMBER_OF_SLIDES_IN_CACHE / 2);

    const promises = getAllChannelDataPromises(start, end, zoomLevel, config);

    return Promise.all(promises)
        .then(thunkAPI.dispatch(chartSlice.actions.setRange({ start: start, end: end })))
        .then(thunkAPI.dispatch(chartSlice.actions.setXScrollPosition(0)));
});
export const jumpToLastSlice = createAsyncThunk('chart/jumpToLastSlice', async (_, thunkAPI) => {
    let { channelsConfig, maxDataSize, zoomLevel } = thunkAPI.getState().app.chart;
    const config = createGetChannelDataConfig(channelsConfig);
    const start = maxDataSize - zoomLevel * Math.ceil(NUMBER_OF_SLIDES_IN_CACHE / 2);

    const promises = getAllChannelDataPromises(start, maxDataSize, zoomLevel, config);
    return Promise.all(promises)
        .then(thunkAPI.dispatch(chartSlice.actions.setRange({ start: start, end: maxDataSize })))
        .then(thunkAPI.dispatch(chartSlice.actions.setXScrollPosition(maxDataSize - zoomLevel)));
});

export const {
    setXScrollPosition,
    setZoomLevel,
    toggleMonotoneInterpolation,
    toggleAppliedAnnotation,
    setChannelYAxisLimits,
    increaseChannelYLimits,
    decreaseChannelYLimits,
    setChannelsOrder,
} = chartSlice.actions;

export default chartSlice.reducer;
